import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp, TrendingDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Quote = ({ q }) => {
  const up = (q.change_percent ?? 0) >= 0;
  return (
    <span className="inline-flex items-center gap-2 px-6 whitespace-nowrap" data-testid={`ticker-${q.symbol}`}>
      <span className="font-semibold text-white tracking-wide">{q.symbol}</span>
      <span className="text-[var(--text-secondary)] text-xs hidden sm:inline">{q.name}</span>
      <span className="text-white tabular-nums">${q.price?.toFixed(2)}</span>
      <span className={`inline-flex items-center gap-0.5 tabular-nums text-sm ${up ? "text-emerald-400" : "text-rose-400"}`}>
        {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {up ? "+" : ""}{q.change_percent?.toFixed(2)}%
      </span>
    </span>
  );
};

export const StockTicker = () => {
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    const load = () => fetch(`${API}/api/quotes`).then((r) => (r.ok ? r.json() : [])).then(setQuotes).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!quotes.length) return <div className="h-12 border-y border-[rgba(124,58,237,0.15)]" data-testid="stock-ticker-loading" />;
  return (
    <div className="relative overflow-hidden border-y border-[rgba(124,58,237,0.15)] bg-[var(--bg-secondary)]/60 py-3 group" data-testid="stock-ticker">
      <div className="flex w-max animate-[ticker_45s_linear_infinite] group-hover:[animation-play-state:paused]">
        {[...quotes, ...quotes].map((q, i) => <Quote key={`${q.symbol}-${i}`} q={q} />)}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[var(--bg-primary)] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[var(--bg-primary)] to-transparent" />
    </div>
  );
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};
const source = (it) => {
  const src = it.source?.startsWith("Google News") ? "" : it.source;
  return [it.company, src].filter((x, i, a) => x && a.indexOf(x) === i).join(" · ");
};

const TopCard = ({ item, i }) => (
  <motion.a href={item.url} target="_blank" rel="noopener noreferrer" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
    className="group relative flex flex-col bg-[var(--bg-card)] border-glow rounded-xl p-6 hover:border-[var(--accent-violet)]/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-300" data-testid={`pulse-top-${i}`}>
    <div className="flex items-center justify-between mb-4">
      <span className="font-display italic text-3xl text-[var(--accent-violet-light)]/40 leading-none">{String(i + 1).padStart(2, "0")}</span>
      <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent-violet-light)] font-semibold">{item.type}</span>
    </div>
    <p className="text-white font-semibold leading-snug mb-2 group-hover:text-[var(--accent-violet-light)] transition-colors">{item.headline}</p>
    <p className="text-sm text-[var(--text-secondary)] flex-1">{item.why}</p>
    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[rgba(124,58,237,0.1)] text-xs text-[var(--text-secondary)]">
      <span className="whitespace-nowrap" data-testid={`pulse-top-date-${i}`}>{fmtDate(item.published_date)}</span>
      <span className="truncate ml-3 text-right">{source(item)}</span>
    </div>
  </motion.a>
);

const ArchiveRow = ({ item, i }) => (
  <a href={item.url} target="_blank" rel="noopener noreferrer" className="group grid grid-cols-[88px_1fr_auto] gap-5 items-start py-4 border-b border-[rgba(124,58,237,0.1)] hover:bg-[var(--accent-violet)]/[0.04] -mx-4 px-4 rounded-lg transition-colors" data-testid={`pulse-archive-${i}`}>
    <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent-violet-light)] font-semibold pt-1">{item.type}</span>
    <div>
      <p className="text-white font-medium leading-snug group-hover:text-[var(--accent-violet-light)] transition-colors">{item.headline}</p>
      <p className="text-sm text-[var(--text-secondary)] mt-1">{item.why}</p>
      <p className="text-xs text-[var(--text-secondary)]/70 mt-1.5">{source(item)}</p>
    </div>
    <ArrowUpRight className="w-4 h-4 text-[var(--text-secondary)] group-hover:text-white transition-colors mt-1" />
  </a>
);

const PAGE = 20;

const PulsePage = () => {
  const [top, setTop] = useState(null);
  const [archive, setArchive] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchPage = (skip, limit) => fetch(`${API}/api/pulse?skip=${skip}&limit=${limit}`).then((r) => r.json());

  useEffect(() => {
    fetchPage(0, 6 + PAGE).then((d) => { setTop(d.items.slice(0, 6)); setArchive(d.items.slice(6)); setTotal(d.total); }).catch(() => setTop([]));
  }, []);

  const loadMore = async () => {
    setLoading(true);
    try { const d = await fetchPage(6 + archive.length, PAGE); setArchive((a) => [...a, ...d.items]); setTotal(d.total); }
    finally { setLoading(false); }
  };

  const groups = archive.reduce((acc, it) => {
    const k = dayLabel(it.published_date);
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});
  const remaining = total - 6 - archive.length;

  return (
    <main className="pt-24 pb-20 min-h-screen" data-testid="pulse-page">
      <StockTicker />
      <div className="max-w-6xl mx-auto px-6 md:px-8 pt-16">
        <p className="text-sm uppercase tracking-[0.25em] text-[var(--accent-violet-light)] font-medium mb-4">Daily AI Briefing</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white italic mb-5" data-testid="pulse-heading">AI Pulse</h1>
        <p className="text-[var(--text-secondary)] text-base md:text-lg max-w-2xl mb-14">New tools, model releases and the moves of the companies shaping the AI economy — curated daily, with one line on why each one matters.</p>
        {top === null ? null : top.length === 0 ? (
          <p className="text-[var(--text-secondary)]" data-testid="pulse-empty">Today's briefing is being curated. Check back soon.</p>
        ) : (
          <>
            <section className="mb-20" data-testid="pulse-top-section">
              <div className="flex items-end justify-between mb-6">
                <h2 className="font-display text-3xl text-white italic">The Top 6</h2>
                <span className="text-xs text-[var(--text-secondary)]">Latest {top.length} · updated {fmtDate(top[0].published_date)}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {top.map((it, i) => <TopCard key={it.url + i} item={it} i={i} />)}
              </div>
            </section>
            <section data-testid="pulse-archive-section">
              <h2 className="font-display text-3xl text-white italic mb-2">Archive</h2>
              <p className="text-sm text-[var(--text-secondary)] mb-8">Earlier AI Pulse briefs, by day.</p>
              {archive.length === 0 ? (
                <p className="text-sm text-[var(--text-secondary)]" data-testid="pulse-archive-empty">Older briefs will appear here as the Pulse grows.</p>
              ) : Object.entries(groups).map(([day, list]) => (
                <div key={day} className="mb-10">
                  <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)] mb-1">{day}</h3>
                  {list.map((it) => <ArchiveRow key={it.url + archive.indexOf(it)} item={it} i={archive.indexOf(it)} />)}
                </div>
              ))}
              {remaining > 0 && (
                <button onClick={loadMore} disabled={loading} className="mt-4 border border-[rgba(124,58,237,0.4)] hover:border-[var(--accent-violet)] text-white px-6 py-2.5 rounded-full text-sm transition-colors disabled:opacity-60" data-testid="pulse-load-more">
                  {loading ? "Loading..." : `Load older briefs (${remaining})`}
                </button>
              )}
            </section>
          </>
        )}
        <p className="text-[11px] text-[var(--text-secondary)]/60 mt-16">Market data provided by Finnhub, delayed and for information only. Not investment advice.</p>
      </div>
    </main>
  );
};

export default PulsePage;
